import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { PasswordField } from '@/components/ui/Field';
import { useForm } from '@/hooks/useForm';
import { PASSWORD_MIN_LENGTH } from '@/types/schemas';

import { useChangePassword } from '../../hooks/useAuthActions';
import { changePasswordFormSchema } from '../../schemas/auth.schema';
import styles from './ChangePasswordForm.module.css';

export interface ChangePasswordFormProps {
  onSuccess?: () => void;
  submitLabel?: string;
}

/** Current password + new password twice. Other devices are signed out by the API. */
export function ChangePasswordForm({
  onSuccess,
  submitLabel = 'Change password',
}: ChangePasswordFormProps) {
  const form = useForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const changePassword = useChangePassword();

  return (
    <form
      className={styles.form}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        const values = form.parse(changePasswordFormSchema);
        if (!values) return;
        changePassword.mutate(
          { currentPassword: values.currentPassword, newPassword: values.newPassword },
          {
            onSuccess: () => {
              form.reset();
              onSuccess?.();
            },
            onError: form.handleError,
          },
        );
      }}
    >
      <PasswordField
        label="Current password"
        autoComplete="current-password"
        required
        {...form.field('currentPassword')}
      />
      <PasswordField
        label="New password"
        autoComplete="new-password"
        hint={`At least ${String(PASSWORD_MIN_LENGTH)} characters. Only you should know it.`}
        required
        {...form.field('newPassword')}
      />
      <PasswordField
        label="Type the new password again"
        autoComplete="new-password"
        required
        {...form.field('confirmPassword')}
      />
      {form.formError && <Alert tone="danger">{form.formError}</Alert>}
      <Button type="submit" isLoading={changePassword.isPending} className={styles.submit}>
        {submitLabel}
      </Button>
    </form>
  );
}
